import { useState, useEffect } from "react";
import propTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheck, FaFile, FaFilePdf, FaEye, FaEyeSlash, FaTrash } from 'react-icons/fa';

export const FileInputWithCheckbox = ({ 
    name, 
    id, 
    label, 
    disabled, 
    setFile,
    file,
    showAdditionalOption = false,
    setAdditionalFile,
    additionalFile,
    additionalLabel,
}) => {
    const [showAdditionalFileInput, setShowAdditionalFileInput] = useState(false);
    const [preview, setPreview] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [additionalPreview, setAdditionalPreview] = useState(null);
    const [showAdditionalPreview, setShowAdditionalPreview] = useState(false);
    const [pdfWarning, setPdfWarning] = useState(false);
    const { t } = useTranslation();

    const variants = {
        hidden: { opacity: 0, y: -20 },
        visible: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -20 },
    }

    useEffect(() => {
        if (!file?.file) {
            setPreview(null);
            return;
        }

        const objectUrl = URL.createObjectURL(file.file);
        setPreview(objectUrl);

        return () => URL.revokeObjectURL(objectUrl);
    }, [file]);

    useEffect(() => {
        if (!additionalFile?.file) {
            setAdditionalPreview(null);
            return;
        }

        const objectUrl = URL.createObjectURL(additionalFile.file);
        setAdditionalPreview(objectUrl);
        setShowAdditionalFileInput(true);

        return () => URL.revokeObjectURL(objectUrl);
    }, [additionalFile]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type === 'application/pdf') {
            setPdfWarning(true);
            setFile(null);
            return;
        } else {
            setPdfWarning(false);
        }
        if (selectedFile) {
            setFile({
                file: selectedFile,
                name: selectedFile.name,
                checked: true
            });
        }
    };

    const handleAdditionalFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type === 'application/pdf') {
            setPdfWarning(true);
            setAdditionalFile(null);
            return;
        } else {
            setPdfWarning(false);
        }
        if (selectedFile) {
            setAdditionalFile({
                file: selectedFile,
                name: selectedFile.name,
                checked: true
            });
        }
    };

    const renderFilePreview = (fileObj, previewUrl) => {
        if (!fileObj?.file) return null;
        
        if (fileObj.file.type.startsWith('image/')) {
            return (
                <div className="relative w-full">
                    <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="w-full h-auto max-h-[500px] object-contain rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                    />
                </div>
            );
        } else if (fileObj.file.type === 'application/pdf') {
            return (
                <div className="relative w-full h-[600px] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <object
                        data={previewUrl}
                        type="application/pdf"
                        className="w-full h-full"
                    >
                        <embed
                            src={previewUrl}
                            type="application/pdf"
                            className="w-full h-full"
                        />
                    </object>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="col-span-2 sm:col-span-2 space-y-4">
            {pdfWarning && (
                <div className="mb-2 p-2 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-xs border border-yellow-300 dark:border-yellow-700">
                    {t('No se pueden procesar archivos PDF en este campo. Solo se permiten imágenes (.jpg, .jpeg, .png).')}
                </div>
            )}
            <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-900 dark:text-white" htmlFor={id}>
                    {label}
                </label>
                {file && (
                    <div className="flex items-center space-x-2">
                        <button
                            type="button"
                            onClick={() => setShowPreview(!showPreview)}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                            title={showPreview ? t('Ocultar preview') : t('Mostrar preview')}
                        >
                            {showPreview ? <FaEyeSlash /> : <FaEye />}
                        </button>
                        <span className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center">
                            <FaCheck className="mr-1" /> {file.name}
                        </span>
                        <button
                            type="button"
                            onClick={() => setFile(null)}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                        >
                            <FaTrash />
                        </button>
                    </div>
                )}
            </div>
            
            {/* Preview Container */}
            <AnimatePresence>
                {file && showPreview && (
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={variants}
                        className="rounded-lg overflow-hidden"
                    >
                        {renderFilePreview(file, preview)}
                        <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center">
                                {file.file.type.startsWith('image/') ? (
                                    <img 
                                        src={preview} 
                                        alt="Thumbnail" 
                                        className="w-8 h-8 object-cover rounded mr-2"
                                    />
                                ) : file.file.type === 'application/pdf' ? (
                                    <FaFilePdf className="w-8 h-8 text-red-500 mr-2" />
                                ) : (
                                    <FaFile className="w-8 h-8 text-gray-400 mr-2" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {file.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {(file.file.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="relative">
                <input 
                    disabled={disabled} 
                    className="hidden" 
                    name={name} 
                    id={id} 
                    type="file"
                    onChange={handleFileChange}
                    accept=".jpg,.jpeg,.png"
                    ref={(input) => {
                        if (input && file?.file) {
                            const dataTransfer = new DataTransfer();
                            dataTransfer.items.add(file.file);
                            input.files = dataTransfer.files;
                        }
                    }}
                />
                <label
                    htmlFor={id}
                    className={`
                        block w-full text-sm cursor-pointer
                        flex items-center justify-center px-4 py-2 rounded-md border-2 border-dashed
                        ${file
                            ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/10'
                            : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
                        }
                        transition-colors duration-200
                    `}
                >
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                        {file ? t('Cambiar archivo') : t('Seleccionar archivo')}
                    </span>
                </label>
            </div>
            
            {showAdditionalOption && (
                <>
                    <div className="block text-sm font-medium text-gray-900 dark:text-white">
                        <label className="relative inline-flex items-center cursor-pointer text-xs text-gray-600 dark:text-gray-400" htmlFor={`${id}_checkbox`}>
                            <input 
                                type="checkbox" 
                                id={`${id}_checkbox`} 
                                name={`${name}_checkbox`} 
                                //Si el input especifico ha recibido un archivo, se marca como checked
                                defaultChecked={additionalFile?.file ? true : false}
                                className="sr-only peer" 
                                onChange={(e) => setShowAdditionalFileInput(e.target.checked)} 
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            <span className="ml-3">{t("Agregar archivo adicional")}</span>
                        </label>
                    </div>
                    
                    <AnimatePresence>
                        {showAdditionalFileInput && (
                            <motion.div
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                variants={variants}
                                transition={{ ease: "easeOut", duration: 0.3 }}
                                className="space-y-4"
                            >
                                <div className="flex justify-between items-center">
                                    <label className="block text-sm font-medium text-gray-900 dark:text-white" htmlFor={`${id}_additional`}>
                                        {additionalLabel || `${label} Adicional`}
                                    </label>
                                    {additionalFile && (
                                        <div className="flex items-center space-x-2">
                                            <button
                                                type="button"
                                                onClick={() => setShowAdditionalPreview(!showAdditionalPreview)}
                                                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                                                title={showAdditionalPreview ? t('Ocultar preview') : t('Mostrar preview')}
                                            >
                                                {showAdditionalPreview ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                            <span className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center">
                                                <FaCheck className="mr-1" /> {additionalFile.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setAdditionalFile(null)}
                                                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Additional File Preview Container */}
                                <AnimatePresence>
                                    {additionalFile && showAdditionalPreview && (
                                        <motion.div
                                            initial="hidden"
                                            animate="visible"
                                            exit="hidden"
                                            variants={variants}
                                            className="rounded-lg overflow-hidden"
                                        >
                                            {renderFilePreview(additionalFile, additionalPreview)}
                                            <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                                                <div className="flex items-center">
                                                    {additionalFile.file.type.startsWith('image/') ? (
                                                        <img 
                                                            src={additionalPreview} 
                                                            alt="Thumbnail" 
                                                            className="w-8 h-8 object-cover rounded mr-2"
                                                        />
                                                    ) : additionalFile.file.type === 'application/pdf' ? (
                                                        <FaFilePdf className="w-8 h-8 text-red-500 mr-2" />
                                                    ) : (
                                                        <FaFile className="w-8 h-8 text-gray-400 mr-2" />
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                            {additionalFile.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {(additionalFile.file.size / 1024 / 1024).toFixed(2)} MB
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <div className="relative">
                                    <input 
                                        disabled={disabled} 
                                        className="hidden" 
                                        name={`${name}_additional`} 
                                        id={`${id}_additional`} 
                                        type="file"
                                        onChange={handleAdditionalFileChange}
                                        accept=".jpg,.jpeg,.png"
                                        ref={(input) => {
                                            if (input && additionalFile?.file) {
                                                const dataTransfer = new DataTransfer();
                                                dataTransfer.items.add(additionalFile.file);
                                                input.files = dataTransfer.files;
                                            }
                                        }}
                                    />
                                    <label
                                        htmlFor={`${id}_additional`}
                                        className={`
                                            block w-full text-sm cursor-pointer
                                            flex items-center justify-center px-4 py-2 rounded-md border-2 border-dashed
                                            ${additionalFile
                                                ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/10'
                                                : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
                                            }
                                            transition-colors duration-200
                                        `}
                                    >
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            {additionalFile ? t('Cambiar archivo') : t('Seleccionar archivo')}
                                        </span>
                                    </label>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}
        </div>
    );
};

FileInputWithCheckbox.propTypes = {
    name: propTypes.string.isRequired,
    id: propTypes.string.isRequired,
    label: propTypes.string.isRequired,
    disabled: propTypes.bool,
    setFile: propTypes.func.isRequired,
    file: propTypes.shape({
        file: propTypes.object,
        name: propTypes.string,
        checked: propTypes.bool
    }),
    setAdditionalFile: propTypes.func,
    additionalFile: propTypes.shape({
        file: propTypes.object,
        name: propTypes.string,
        checked: propTypes.bool
    }),
    showAdditionalOption: propTypes.bool,
    additionalLabel: propTypes.string,
};
